import {
  getByAccountId,
  remove,
  save,
} from 'modules/host-payout-methods/clients/payoutAccounts';
import {
  PayoutAccount,
  PayoutAccountFromGet,
} from 'modules/host-payout-methods/models/PayoutAccount';
import { ChangeEvent } from 'react';

import { getByCountry } from '../clients/payoutMethods';
import { DispatchedActions } from '../reducers/actions';

export interface Actions {
  formInputChange: (arg: { event: ChangeEvent }) => void;
  setPayoutMethod: (arg: { payoutMethodId: string }) => void;
  setBillingCountry: (arg: { billingCountry: string }) => void;
  fetchAvailablePayoutMethods: (arg: { country: string }) => void;
  fetchPayoutAccounts: (arg: { accountId: string }) => void;
  fetchPayoutMethods: () => void;
  setPayoutAccount: (arg: { payoutAccount: PayoutAccount }) => void;
  submit: (arg: {
    payoutAccount: PayoutAccount;
    accountId: string;
  }) => Promise<void>;
  togglePreferredPayoutAccount: () => void;
  deletePayoutAccount: (arg: {
    payoutAccount: PayoutAccount;
    accountId: string;
  }) => Promise<void>;
  reset: () => void;
  goNext: () => void;
  goBack: () => void;
}

export default function actionsFactory(
  dispatch: (action: DispatchedActions) => void,
): Actions {
  return {
    formInputChange: ({ event }) => {
      dispatch({ type: 'FORM_INPUT_CHANGE', event });
    },
    setPayoutMethod: ({ payoutMethodId }) => {
      dispatch({ type: 'SET_PAYOUT_METHOD', payoutMethodId });
    },
    setBillingCountry: ({ billingCountry }) => {
      dispatch({ type: 'SET_BILLING_COUNTRY', billingCountry });
    },
    fetchAvailablePayoutMethods: ({ country }) => {
      const fetchAvailablePayoutMethods = ({
        country,
      }: {
        country: string;
      }) => {
        return getByCountry({ countryCode: country }).then(({ data }) => data);
      };
      dispatch({ type: 'FETCH_AVAILABLE_PAYOUT_METHODS_PER_COUNTRY' });
      fetchAvailablePayoutMethods({ country })
        .then((res) => {
          dispatch({
            type: 'FETCH_AVAILABLE_PAYOUT_METHODS_PER_COUNTRY:SUCCESS',
            country: country,
            payoutMethodsPerCountry: res,
          });
        })
        .catch((err) => {
          dispatch({
            type: 'FETCH_AVAILABLE_PAYOUT_METHODS_PER_COUNTRY:ERROR',
            error: err,
          });
        });
    },
    fetchPayoutMethods: () => {
      const fetchPayoutMethods = (mock: boolean) => {
        if (mock) {
          return Promise.resolve(require('../mocks/payout-methods.json'));
        }
        throw Error('Not supported yet');
      };
      dispatch({ type: 'FETCH_PAYOUT_METHODS' });
      fetchPayoutMethods(true)
        .then((res) => {
          dispatch({
            type: 'FETCH_PAYOUT_METHODS:SUCCESS',
            payoutMethods: res,
          });
        })
        .catch((err) => {
          if (!err.isCanceled) {
            dispatch({ type: 'FETCH_PAYOUT_METHODS:ERROR', error: 'Error!' });
          }
        });
    },
    fetchPayoutAccounts: ({ accountId }) => {
      const fetchPayoutAccounts = (
        { accountId }: { accountId: string },
        mock: boolean,
      ): Promise<PayoutAccountFromGet[]> => {
        if (mock) {
          return Promise.resolve(require('../mocks/payout-accounts.json'));
        }
        return getByAccountId({ accountId: accountId }).then(
          ({ data }) => data,
        );
      };

      dispatch({ type: 'FETCH_PAYOUT_ACCOUNTS' });
      fetchPayoutAccounts({ accountId }, false)
        .then((res) => {
          dispatch({
            type: 'FETCH_PAYOUT_ACCOUNTS:SUCCESS',
            payoutAccounts: res,
          });
        })
        .catch((err) => {
          dispatch({ type: 'FETCH_PAYOUT_ACCOUNTS:ERROR', error: err });
        });
    },
    setPayoutAccount: ({ payoutAccount }) => {
      dispatch({ type: 'SET_PAYOUT_ACCOUNT', payoutAccount });
    },
    submit: ({ payoutAccount, accountId }) => {
      dispatch({ type: 'SUBMIT' });
      return save({ payoutAccount, accountId })
        .then(() => {
          dispatch({ type: 'SUBMIT:SUCCESS', payoutAccount });
        })
        .catch((err) => {
          dispatch({
            type: 'SUBMIT:ERROR',
            payoutAccount,
            error: err.responseJson.message,
          });
          throw Error;
        });
    },
    deletePayoutAccount: ({ payoutAccount, accountId }) => {
      dispatch({ type: 'DELETE_PAYOUT_ACCOUNT' });
      return remove({ payoutAccount, accountId })
        .then(() => {
          dispatch({
            type: 'DELETE_PAYOUT_ACCOUNT:SUCCESS',
            id: payoutAccount.id,
          });
        })
        .catch((err) => {
          dispatch({ type: 'DELETE_PAYOUT_ACCOUNT:ERROR', error: err });
        });
    },
    togglePreferredPayoutAccount: () => {
      dispatch({ type: 'TOGGLE_PREFERRED_PAYOUT_ACCOUNT' });
    },
    reset: () => {
      dispatch({ type: 'CLEAR_CURRENT_PAYOUT' });
    },
    goNext: () => {
      dispatch({ type: 'ON_NEXT' });
    },
    goBack: () => {
      dispatch({ type: 'ON_BACK' });
    },
  };
}
